import React from 'react'
import { ClikArtPlacementProvider } from '@/contexts/clikart-placement-context'
import { ColorPlacementProvider } from '@/contexts/color-placement-context'
import { PhotoPlacementProvider } from '@/contexts/photo-placement-context'
import { BackgroundPlacementProvider } from '@/contexts/background-placement-context'
import { TemplatePlacementProvider } from '@/contexts/template-placement-context'

type ClikArtProps = React.ComponentProps<typeof ClikArtPlacementProvider>
type ColorProps = React.ComponentProps<typeof ColorPlacementProvider>
type PhotoProps = React.ComponentProps<typeof PhotoPlacementProvider>
type BackgroundProps = React.ComponentProps<typeof BackgroundPlacementProvider>
type TemplateProps = React.ComponentProps<typeof TemplatePlacementProvider>

/**
 * The drag-and-drop placement contexts the editor body sits in — ClikArt, then photos, then
 * backgrounds, then templates — so a drop from any right-panel tray can land on the canvas. The
 * item trays announce through the same screen-reader live region.
 */
export function EditorPlacementProviders({
  shapeManagerRef,
  centerCanvasRef,
  spreadIdRef,
  spreadPlacementBounds,
  snapToGrid,
  onScreenReaderAnnounce,
  onFillShapeWithColor,
  onFillShapeWithClikArt,
  onClikArtDropBlocked,
  onFillShapeWithPhoto,
  onPhotoDropBlocked,
  onPlaceTemplate,
  spreadId,
  checkpointMetadata,
  isCheckpointLoaded,
  restoreSpreadBackgroundsRef,
  flushSpreadBackgroundWritesRef,
  onSpreadBackgroundsChanged,
  children,
}: {
  shapeManagerRef: PhotoProps['shapeManagerRef']
  centerCanvasRef: PhotoProps['centerCanvasRef']
  spreadIdRef: PhotoProps['spreadIdRef']
  spreadPlacementBounds: PhotoProps['spreadPlacementBounds']
  snapToGrid: PhotoProps['snapToGrid']
  onScreenReaderAnnounce: (message: string) => void
  onFillShapeWithColor: ColorProps['onFillShape']
  onFillShapeWithClikArt: ClikArtProps['onFillShape']
  onClikArtDropBlocked: ClikArtProps['onBlockedDrop']
  onFillShapeWithPhoto: PhotoProps['onFillShape']
  onPhotoDropBlocked: PhotoProps['onBlockedDrop']
  onPlaceTemplate: TemplateProps['onPlaceTemplate']
  spreadId: BackgroundProps['spreadId']
  checkpointMetadata: BackgroundProps['checkpointMetadata']
  isCheckpointLoaded: BackgroundProps['isCheckpointLoaded']
  restoreSpreadBackgroundsRef: BackgroundProps['restoreSpreadBackgroundsRef']
  flushSpreadBackgroundWritesRef: BackgroundProps['flushSpreadBackgroundWritesRef']
  onSpreadBackgroundsChanged: BackgroundProps['onSpreadBackgroundsChanged']
  children: React.ReactNode
}) {
  return (
    <ColorPlacementProvider
      shapeManagerRef={shapeManagerRef}
      centerCanvasRef={centerCanvasRef}
      onFillShape={onFillShapeWithColor}
    >
      <ClikArtPlacementProvider
        shapeManagerRef={shapeManagerRef}
        centerCanvasRef={centerCanvasRef}
        snapToGrid={snapToGrid}
        onScreenReaderAnnounce={onScreenReaderAnnounce}
        onFillShape={onFillShapeWithClikArt}
        onBlockedDrop={onClikArtDropBlocked}
      >
        <PhotoPlacementProvider
          shapeManagerRef={shapeManagerRef}
          centerCanvasRef={centerCanvasRef}
          spreadIdRef={spreadIdRef}
          spreadPlacementBounds={spreadPlacementBounds}
          snapToGrid={snapToGrid}
          onScreenReaderAnnounce={onScreenReaderAnnounce}
          onFillShape={onFillShapeWithPhoto}
          onBlockedDrop={onPhotoDropBlocked}
        >
          <BackgroundPlacementProvider
            spreadId={spreadId}
            checkpointMetadata={checkpointMetadata}
            isCheckpointLoaded={isCheckpointLoaded}
            restoreSpreadBackgroundsRef={restoreSpreadBackgroundsRef}
            flushSpreadBackgroundWritesRef={flushSpreadBackgroundWritesRef}
            onSpreadBackgroundsChanged={onSpreadBackgroundsChanged}
          >
            <TemplatePlacementProvider
              centerCanvasRef={centerCanvasRef}
              shapeManagerRef={shapeManagerRef}
              spreadId={spreadId}
              onScreenReaderAnnounce={onScreenReaderAnnounce}
              onPlaceTemplate={onPlaceTemplate}
            >
              {children}
            </TemplatePlacementProvider>
          </BackgroundPlacementProvider>
        </PhotoPlacementProvider>
      </ClikArtPlacementProvider>
    </ColorPlacementProvider>
  )
}
